import React, { FC } from 'react';
import cx from 'classnames';
import HighlightedMentions from '../HighlightedMentions';
import { highlightSearchTextInHtml } from '../HighlightedMentions/highlightHtml';
import { Props as HighlightedMentionsProps } from '../HighlightedMentions/types';
import {
  sanitizeDescriptionHtml,
  markdownToHtml,
  looksLikeMarkdown,
  normalizeLineTerminators
} from '../../../clients/sidebar/components/RichTextDescription/utils/serialization';
import { highlightCodeBlocksHtml } from '../../../clients/sidebar/components/RichTextDescription/utils/highlightSnippetCode';
import proseStyles from '../../../clients/sidebar/components/RichTextDescription/richTextProse.module.css';
import styles from './index.module.css';

export type TaskDescriptionSnippetProps = {
  title?: string | null;
  description: string;
  descriptionHtml?: string | null;
  variant: 'kanban' | 'list' | 'pin';
} & HighlightedMentionsProps;

export const TaskDescriptionSnippet: FC<TaskDescriptionSnippetProps> = ({
  title,
  description,
  descriptionHtml,
  variant,
  className,
  icon,
  searchHighlightText,
  ...mentionProps
}) => {
  const normalizedTitle = title == null ? title : normalizeLineTerminators(title);
  const normalizedDescription = normalizeLineTerminators(description || '');
  const normalizedDescriptionHtml =
    descriptionHtml == null
      ? descriptionHtml
      : normalizeLineTerminators(descriptionHtml);

  const hasTitle = Boolean(normalizedTitle?.trim());
  const rawHtml = normalizedDescriptionHtml?.trim()
    ? normalizedDescriptionHtml.trim()
    : null;
  const fromServerHtml =
    !hasTitle && rawHtml ? sanitizeDescriptionHtml(rawHtml) : null;
  const fromDescriptionMarkdown =
    !hasTitle && !fromServerHtml && looksLikeMarkdown(normalizedDescription)
      ? markdownToHtml(normalizedDescription)
      : null;
  const safeHtml = fromServerHtml || fromDescriptionMarkdown
    ? highlightCodeBlocksHtml(fromServerHtml || fromDescriptionMarkdown || '')
    : null;

  const kanbanTypo = variant === 'kanban' ? styles.kanbanTypo : null;
  const listSingleLineClass = variant === 'list' ? styles.listSingleLine : null;
  const proseVariantClass =
    variant === 'kanban'
      ? styles.proseKanban
      : variant === 'list'
      ? proseStyles.proseRootListRow
      : null;

  if (hasTitle) {
    return (
      <HighlightedMentions
        {...mentionProps}
        searchHighlightText={searchHighlightText}
        description={normalizedTitle!.trim()}
        className={cx(className, kanbanTypo, listSingleLineClass)}
        icon={icon}
      />
    );
  }

  if (safeHtml) {
    return (
      <div
        className={cx(
          proseStyles.proseRoot,
          proseStyles.previewReadOnly,
          proseVariantClass,
          kanbanTypo,
          listSingleLineClass,
          className
        )}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: highlightSearchTextInHtml(safeHtml, searchHighlightText)
        }}
      />
    );
  }

  return (
    <HighlightedMentions
      {...mentionProps}
      searchHighlightText={searchHighlightText}
      description={normalizedDescription}
      className={cx(className, kanbanTypo, listSingleLineClass)}
      icon={icon}
    />
  );
};

export default TaskDescriptionSnippet;
